import {
  and,
  document_segments,
  eq,
  generateULID,
  processing_review_tasks,
  split_runs,
  splitter_sections,
  type PrimaryDB,
} from "@ocr-monorepo/db";
import {
  ensureProcessingWorkItem,
  recordStageReceipt,
  sha256Hex,
  stableJson,
  transitionProcessingRun,
} from "./processing-runs";
import { Effect } from "effect";
import { z } from "zod";
import {
  routedLogicalSplitterResultSchema,
  type CompiledPageRangeSplit,
} from "./splitters";

export type PersistPageRangeSplitInput = {
  runId: string;
  documentId: number;
  splitterVersionId: string;
  configSnapshotId: string;
  destinationId: string;
  compiled: CompiledPageRangeSplit;
};

export type PersistedPageRangeSplit = {
  splitRunId: string;
  status: "succeeded" | "waiting_for_review";
  segmentIds: string[];
  segmentCount: number;
  reviewCount: number;
};

export async function persistPageRangeSplit(
  db: PrimaryDB,
  input: PersistPageRangeSplitInput,
): Promise<PersistedPageRangeSplit> {
  const startedAt = new Date();
  const proposedSplitRunId = generateULID();
  await db
    .insert(split_runs)
    .values({
      id: proposedSplitRunId,
      processing_run_id: input.runId,
      splitter_version_id: input.splitterVersionId,
      status: "running",
      startedAt,
      createdAt: startedAt,
      updatedAt: startedAt,
    })
    .onConflictDoNothing({
      target: [split_runs.processing_run_id, split_runs.splitter_version_id],
    });

  const [storedSplitRun] = await db
    .select({ id: split_runs.id })
    .from(split_runs)
    .where(
      and(
        eq(split_runs.processing_run_id, input.runId),
        eq(split_runs.splitter_version_id, input.splitterVersionId),
      ),
    )
    .limit(1);
  if (!storedSplitRun) throw new Error("Unable to persist splitter run");

  const publishedSections = await db
    .select({
      id: splitter_sections.id,
      key: splitter_sections.section_key,
    })
    .from(splitter_sections)
    .where(eq(splitter_sections.splitter_version_id, input.splitterVersionId));
  const sectionIdByKey = new Map(
    publishedSections.map((section) => [section.key, section.id]),
  );

  for (const segment of input.compiled.segments) {
    const sectionId = sectionIdByKey.get(segment.sectionKey);
    if (!sectionId) {
      throw new Error(
        `Published splitter section ${segment.sectionKey} is unavailable`,
      );
    }
    await db
      .insert(document_segments)
      .values({
        id: generateULID(),
        split_run_id: storedSplitRun.id,
        processing_run_id: input.runId,
        document_id: input.documentId,
        splitter_section_id: sectionId,
        section_key: segment.sectionKey,
        section_name: segment.sectionName,
        segment_key: segment.segmentKey,
        ordinal: segment.ordinal,
        page_spans: segment.pageSpans,
        page_count: segment.pageCount,
        confidence: 100,
        review_status: segment.reviewRequired ? "pending" : "not_required",
        destination_table_id: segment.destinationTableId,
        createdAt: startedAt,
        updatedAt: startedAt,
      })
      .onConflictDoNothing({
        target: [document_segments.split_run_id, document_segments.segment_key],
      });

    await ensureProcessingWorkItem(db, {
      runId: input.runId,
      destinationId: input.destinationId,
      tableId: segment.destinationTableId,
      schemaRevisionId: input.configSnapshotId,
      segmentId: segment.segmentKey,
      status: "queued",
    });
  }

  if (input.compiled.reviewRequired) {
    await db
      .insert(processing_review_tasks)
      .values({
        id: generateULID(),
        run_id: input.runId,
        document_id: input.documentId,
        scope: "split",
        scope_id: storedSplitRun.id,
        reason_code: "SPLIT_UNASSIGNED_PAGES",
        candidates: [
          {
            kind: "unassigned_pages",
            pageSpans: input.compiled.unassignedPageSpans,
          },
        ],
        status: "open",
        createdAt: startedAt,
        updatedAt: startedAt,
      })
      .onConflictDoNothing({
        target: [
          processing_review_tasks.run_id,
          processing_review_tasks.scope,
          processing_review_tasks.scope_id,
        ],
      });
  }

  const inputHash = await sha256Hex(
    stableJson({
      splitterVersionId: input.splitterVersionId,
      configSnapshotId: input.configSnapshotId,
      pageCount: input.compiled.pageCount,
    }),
  );
  const resultHash = await sha256Hex(
    stableJson({
      segments: input.compiled.segments,
      unassignedPageSpans: input.compiled.unassignedPageSpans,
      overlaps: input.compiled.overlaps,
    }),
  );
  await recordStageReceipt(db, {
    runId: input.runId,
    stage: "segment",
    inputHash,
    resultHash,
    status: "succeeded",
    metrics: {
      strategy: "page_range",
      segmentCount: input.compiled.segments.length,
      uniqueAssignedPageCount: input.compiled.uniqueAssignedPageCount,
      downstreamPageCount: input.compiled.downstreamPageCount,
      reviewRequired: input.compiled.reviewRequired,
    },
    startedAt,
  });

  const status = input.compiled.reviewRequired
    ? "waiting_for_review"
    : "succeeded";
  const completedAt = new Date();
  await db
    .update(split_runs)
    .set({
      status,
      segment_count: input.compiled.segments.length,
      review_count: input.compiled.reviewRequired ? 1 : 0,
      completedAt,
      updatedAt: completedAt,
    })
    .where(eq(split_runs.id, storedSplitRun.id));
  await transitionProcessingRun(db, {
    runId: input.runId,
    status: input.compiled.reviewRequired ? "waiting_for_review" : "running",
    stage: input.compiled.reviewRequired ? "segment" : "plan",
    failureCode: input.compiled.reviewRequired
      ? "SPLIT_UNASSIGNED_PAGES"
      : null,
    failureMessage: input.compiled.reviewRequired
      ? "One or more pages require splitter review"
      : null,
  });

  const storedSegments = await db
    .select({ id: document_segments.id })
    .from(document_segments)
    .where(eq(document_segments.split_run_id, storedSplitRun.id));

  return {
    splitRunId: storedSplitRun.id,
    status,
    segmentIds: storedSegments.map((segment) => segment.id),
    segmentCount: storedSegments.length,
    reviewCount: input.compiled.reviewRequired ? 1 : 0,
  };
}

export const persistLogicalSplitInputSchema = z.object({
  runId: z.string().min(1),
  documentId: z.number().int().positive(),
  splitterVersionId: z.string().min(1),
  configSnapshotId: z.string().min(1),
  compiled: routedLogicalSplitterResultSchema,
});
export type PersistLogicalSplitInput = z.infer<
  typeof persistLogicalSplitInputSchema
>;

export async function persistLogicalSplit(
  db: PrimaryDB,
  rawInput: PersistLogicalSplitInput,
): Promise<PersistedPageRangeSplit> {
  const input = persistLogicalSplitInputSchema.parse(rawInput);
  const startedAt = new Date();
  const proposedSplitRunId = generateULID();
  await db
    .insert(split_runs)
    .values({
      id: proposedSplitRunId,
      processing_run_id: input.runId,
      splitter_version_id: input.splitterVersionId,
      status: "running",
      startedAt,
      createdAt: startedAt,
      updatedAt: startedAt,
    })
    .onConflictDoNothing({
      target: [split_runs.processing_run_id, split_runs.splitter_version_id],
    });

  const [storedSplitRun] = await db
    .select({ id: split_runs.id })
    .from(split_runs)
    .where(
      and(
        eq(split_runs.processing_run_id, input.runId),
        eq(split_runs.splitter_version_id, input.splitterVersionId),
      ),
    )
    .limit(1);
  if (!storedSplitRun) throw new Error("Unable to persist logical splitter run");

  const publishedSections = await db
    .select({ id: splitter_sections.id, key: splitter_sections.section_key })
    .from(splitter_sections)
    .where(eq(splitter_sections.splitter_version_id, input.splitterVersionId));
  const sectionIdByKey = new Map(
    publishedSections.map((section) => [section.key, section.id]),
  );

  await Effect.runPromise(
    Effect.forEach(
      input.compiled.segments,
      (segment) =>
        Effect.tryPromise({
          try: async () => {
            const sectionId = sectionIdByKey.get(segment.sectionTypeKey);
            const reviewRequired =
              segment.boundaryReviewRequired || segment.routingReviewRequired;
            await db
              .insert(document_segments)
              .values({
                id: generateULID(),
                split_run_id: storedSplitRun.id,
                processing_run_id: input.runId,
                document_id: input.documentId,
                splitter_section_id: sectionId ?? null,
                section_key: segment.sectionTypeKey,
                section_name: segment.sectionTypeName,
                segment_key: segment.segmentKey,
                ordinal: segment.ordinal,
                page_spans: [{ start: segment.startPage, end: segment.endPage }],
                page_count: segment.pageCount,
                confidence: Math.min(
                  segment.confidence,
                  segment.routingConfidence ?? segment.confidence,
                ),
                evidence: [
                  { kind: "boundary", reasoning: segment.reasoning },
                  { kind: "routing", candidates: segment.candidates },
                ],
                review_status: reviewRequired ? "pending" : "not_required",
                destination_table_id: segment.destinationTableId,
                createdAt: startedAt,
                updatedAt: startedAt,
              })
              .onConflictDoNothing({
                target: [
                  document_segments.split_run_id,
                  document_segments.segment_key,
                ],
              });

            if (reviewRequired) {
              await db
                .insert(processing_review_tasks)
                .values({
                  id: generateULID(),
                  run_id: input.runId,
                  document_id: input.documentId,
                  scope: "segment_route",
                  scope_id: segment.segmentKey,
                  reason_code: segment.boundaryReviewRequired
                    ? "SPLIT_BOUNDARY_LOW_CONFIDENCE"
                    : "SEGMENT_ROUTE_UNCERTAIN",
                  candidates: segment.candidates,
                  confidence:
                    segment.routingConfidence ?? segment.confidence,
                  status: "open",
                  createdAt: startedAt,
                  updatedAt: startedAt,
                })
                .onConflictDoNothing({
                  target: [
                    processing_review_tasks.run_id,
                    processing_review_tasks.scope,
                    processing_review_tasks.scope_id,
                  ],
                });
            }
          },
          catch: (error) =>
            error instanceof Error ? error : new Error(String(error)),
        }),
      { concurrency: 5, discard: true },
    ),
  );

  const reviewCount = input.compiled.segments.filter(
    (segment) =>
      segment.boundaryReviewRequired || segment.routingReviewRequired,
  ).length;
  const status = reviewCount > 0 ? "waiting_for_review" : "succeeded";
  const completedAt = new Date();
  await db
    .update(split_runs)
    .set({
      status,
      segment_count: input.compiled.segments.length,
      review_count: reviewCount,
      completedAt,
      updatedAt: completedAt,
    })
    .where(eq(split_runs.id, storedSplitRun.id));

  const inputHash = await sha256Hex(
    stableJson({
      splitterVersionId: input.splitterVersionId,
      configSnapshotId: input.configSnapshotId,
      pageCount: input.compiled.pageCount,
    }),
  );
  const resultHash = await sha256Hex(stableJson(input.compiled));
  await recordStageReceipt(db, {
    runId: input.runId,
    stage: "segment",
    inputHash,
    resultHash,
    status: "succeeded",
    provider: "mistral",
    model: "logical-splitter",
    metrics: {
      strategy: "logical",
      segmentCount: input.compiled.segments.length,
      reviewCount,
    },
    startedAt,
  });
  await transitionProcessingRun(db, {
    runId: input.runId,
    status: reviewCount > 0 ? "waiting_for_review" : "running",
    stage: "route",
    failureCode: reviewCount > 0 ? "SPLITTER_REVIEW_REQUIRED" : null,
    failureMessage:
      reviewCount > 0 ? `${reviewCount} sections require review` : null,
  });

  const storedSegments = await db
    .select({ id: document_segments.id })
    .from(document_segments)
    .where(eq(document_segments.split_run_id, storedSplitRun.id));
  return {
    splitRunId: storedSplitRun.id,
    status,
    segmentIds: storedSegments.map((segment) => segment.id),
    segmentCount: storedSegments.length,
    reviewCount,
  };
}
